import { useMemo, useState } from "react";
import { Download, Mail, Filter, ArrowDownUp, PenTool, CheckCircle2, AlertTriangle, Clock, Layers, Search } from "lucide-react";
import { downloadReportPdf } from "@/lib/reportPdf";
import EmailModal from "@/components/EmailModal";
import DetailDrawer from "@/components/DetailDrawer";

/**
 * PpfCommandCenter — sortable/filterable table for PPFs with:
 *  - Color-coded status pills (Overdue / Due soon / Open / Complete)
 *  - Per-row 1-tap PDF download and Email actions
 *  - Sort by Due date, Priority, Site, Status
 *  - Filter by Site + Status quick-tabs
 */
export default function PpfCommandCenter({ ppfs = [], onUpdated, testid = "ppf-command-center" }) {
  const [status, setStatus] = useState("all");
  const [site, setSite] = useState("all");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("due");
  const [sortDir, setSortDir] = useState("asc");
  const [emailFor, setEmailFor] = useState(null);
  const [detail, setDetail] = useState(null);

  const withStatus = useMemo(() => ppfs.map((p) => ({ ...p, _s: derive(p) })), [ppfs]);

  const sites = useMemo(() => {
    const s = new Set(withStatus.map((p) => p.site_code || p.site).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [withStatus]);

  const counts = useMemo(() => ({
    all: withStatus.length,
    overdue: withStatus.filter((p) => p._s.tone === "overdue").length,
    due: withStatus.filter((p) => p._s.tone === "due").length,
    open: withStatus.filter((p) => p._s.tone === "open").length,
    complete: withStatus.filter((p) => p._s.tone === "complete").length,
  }), [withStatus]);

  const filtered = useMemo(() => {
    let arr = withStatus;
    if (status !== "all") arr = arr.filter((p) => p._s.tone === status);
    if (site !== "all") arr = arr.filter((p) => (p.site_code || p.site) === site);
    if (q) {
      const needle = q.toLowerCase();
      arr = arr.filter((p) => [
        p.title, p.description, p.asset_name, p.asset_id,
        p.assignee_name, p.site_code, p.site, p.id,
      ].some((v) => String(v || "").toLowerCase().includes(needle)));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (p) => {
      if (sortKey === "due") return new Date(p.due_at || p.due_date || 0).getTime();
      if (sortKey === "priority") return prioRank(p.priority || p.severity);
      if (sortKey === "site") return String(p.site_code || p.site || "");
      if (sortKey === "status") return prioTone(p._s.tone);
      return 0;
    };
    return [...arr].sort((a, b) => (val(a) > val(b) ? dir : val(a) < val(b) ? -dir : 0));
  }, [withStatus, status, site, q, sortKey, sortDir]);

  const setSort = (k) => {
    if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  return (
    <section className="panel-heavy p-5 md:p-6" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
          <Layers className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted">Planner command center</div>
          <div className="font-display font-black text-xl tracking-tight">Planning Requests</div>
        </div>
        <div className="flex items-center gap-2">
          <select className="input !py-2 !px-3 !text-xs" value={site} onChange={(e) => setSite(e.target.value)} data-testid="ppf-filter-site">
            {sites.map((s) => <option key={s} value={s}>{s === "all" ? "All sites" : s}</option>)}
          </select>
        </div>
      </div>

      {/* Status quick-tabs */}
      <div className="flex flex-wrap gap-2 mb-3">
        <StatusTab active={status === "all"} onClick={() => setStatus("all")} label="All" n={counts.all} />
        <StatusTab active={status === "overdue"} onClick={() => setStatus("overdue")} label="Overdue" n={counts.overdue} tone="overdue" />
        <StatusTab active={status === "due"} onClick={() => setStatus("due")} label="Due soon" n={counts.due} tone="due" />
        <StatusTab active={status === "open"} onClick={() => setStatus("open")} label="Open" n={counts.open} tone="open" />
        <StatusTab active={status === "complete"} onClick={() => setStatus("complete")} label="Complete" n={counts.complete} tone="complete" />
        <div className="flex-1" />
        <div className="panel-flat px-3 flex items-center gap-2 min-w-[200px]">
          <Search className="w-3.5 h-3.5 text-muted" />
          <input className="w-full py-1.5 bg-transparent outline-none text-sm" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ppf-search" />
        </div>
      </div>

      {/* Header row */}
      <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-muted font-black">
        <HeadCell col="col-span-4" label="Request" k="title" sortKey={sortKey} sortDir={sortDir} onClick={setSort} />
        <HeadCell col="col-span-2" label="Site" k="site" sortKey={sortKey} sortDir={sortDir} onClick={setSort} />
        <HeadCell col="col-span-2" label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={setSort} />
        <HeadCell col="col-span-2" label="Due" k="due" sortKey={sortKey} sortDir={sortDir} onClick={setSort} />
        <div className="col-span-2 text-right">Actions</div>
      </div>

      {/* Rows */}
      {filtered.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted mt-2">No Planning Requests match this filter.</div>
      ) : (
        <div className="grid gap-2" data-testid="ppf-rows">
          {filtered.map((p, i) => (
            <div
              key={p.id || p._id || i}
              className="panel p-3.5 md:p-4 grid grid-cols-1 md:grid-cols-12 gap-2 md:items-center"
              data-testid={`ppf-row-${p.id || i}`}
            >
              <button className="md:col-span-4 text-left min-w-0" onClick={() => setDetail(p)}>
                <div className="font-display font-black text-sm md:text-base truncate">
                  {p.title || p.description || p.asset_name || `Planning Request #${String(p.id || "").slice(0, 6)}`}
                </div>
                <div className="text-[11px] font-mono-tight text-muted truncate">
                  {p.asset_id || p.asset_name || ""}{p.assignee_name ? ` · ${p.assignee_name}` : ""}
                </div>
              </button>
              <div className="md:col-span-2 text-sm font-mono-tight truncate">{p.site_code || p.site || "—"}</div>
              <div className="md:col-span-2"><StatusPill tone={p._s.tone} label={p._s.label} /></div>
              <div className="md:col-span-2 text-sm font-mono-tight">{fmt(p.due_at || p.due_date)}</div>
              <div className="md:col-span-2 flex md:justify-end gap-2">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={(e) => { e.stopPropagation(); downloadReportPdf(p, "PPF"); }}
                  title="Download PDF"
                  data-testid={`ppf-download-${p.id || i}`}
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={(e) => { e.stopPropagation(); setEmailFor(p); }}
                  title="Email PDF"
                  data-testid={`ppf-email-${p.id || i}`}
                >
                  <Mail className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <EmailModal
        open={!!emailFor}
        onClose={() => setEmailFor(null)}
        item={emailFor || {}}
        kind="PPF"
        itemLabel={(it) => it.title || it.description || "PPF"}
      />

      {detail && (
        <DetailDrawer
          item={detail}
          onClose={() => setDetail(null)}
          onUpdated={() => { setDetail(null); onUpdated?.(); }}
          itemLabel={(it) => it.title || it.description || "PPF"}
          resourceName="PPF"
          signOffConfig={{
            endpoint: (it) => `/ppfs/${it.id || it._id}/complete`,
            label: "Complete & Sign PPF",
            verb: "complete",
            payload: ({ signature, notes, attach }) => ({
              completed_by_sig: signature,
              completion_notes: notes,
              parts_used: (attach || []).map((a) => a.name || "photo"),
            }),
          }}
          sendBackConfig={{
            endpoint: (it) => `/ppfs/${it.id || it._id}/send-back`,
            label: "Send Back",
            verb: "send back",
            payload: ({ notes }) => ({ reason: notes }),
          }}
        />
      )}
    </section>
  );
}

function derive(p) {
  const s = String(p.status || p.stage || "").toLowerCase();
  const done = ["closed", "complete", "done", "resolved", "signed"].some((k) => s.includes(k));
  if (done) return { tone: "complete", label: "Complete" };
  const due = p.due_at || p.due_date;
  if (due) {
    const dt = new Date(due).getTime();
    const now = Date.now();
    const days = (dt - now) / 86400000;
    if (dt < now) return { tone: "overdue", label: "Overdue" };
    if (days <= 3) return { tone: "due", label: "Due soon" };
  }
  return { tone: "open", label: "Open" };
}
function StatusPill({ tone, label }) {
  const styles = {
    overdue: { bg: "#5a0f0f", border: "#a51919", color: "#ffb4b4", icon: <AlertTriangle className="w-3 h-3" /> },
    due: { bg: "#4a2a06", border: "#f58220", color: "#ffcf99", icon: <Clock className="w-3 h-3" /> },
    open: { bg: "#141414", border: "#2a2a2a", color: "#c9c9c9", icon: <PenTool className="w-3 h-3" /> },
    complete: { bg: "#0a2a12", border: "#1e6b32", color: "#8be0a0", icon: <CheckCircle2 className="w-3 h-3" /> },
  }[tone] || { bg: "#141414", border: "#2a2a2a", color: "#c9c9c9", icon: null };
  return (
    <span className="pill inline-flex items-center gap-1.5"
          style={{ background: styles.bg, borderColor: styles.border, color: styles.color, borderWidth: 1, borderStyle: "solid" }}>
      {styles.icon}{label}
    </span>
  );
}
function StatusTab({ active, onClick, label, n, tone }) {
  const toneColor = { overdue: "#f87171", due: "#f5a020", open: "#a1a1aa", complete: "#4ade80" }[tone];
  return (
    <button
      onClick={onClick}
      className="pill !text-xs !py-1.5 !px-3"
      style={{
        background: active ? "var(--primary)" : "transparent",
        color: active ? "#0a0a0a" : "var(--fg)",
        border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
        fontWeight: 700,
      }}
    >
      {tone && <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? "#0a0a0a" : toneColor }} />}
      {label} <span className="opacity-70">· {n}</span>
    </button>
  );
}
function HeadCell({ col, label, k, sortKey, sortDir, onClick }) {
  const active = sortKey === k;
  return (
    <button className={`${col} text-left flex items-center gap-1 hover:text-white`} onClick={() => onClick(k)}>
      {label}
      <ArrowDownUp className="w-3 h-3" style={{ opacity: active ? 1 : 0.3, color: active ? "var(--primary)" : "inherit" }} />
      {active && <span className="text-[9px]" style={{ color: "var(--primary)" }}>{sortDir === "asc" ? "▲" : "▼"}</span>}
    </button>
  );
}
function prioRank(p) {
  const s = String(p || "").toLowerCase();
  if (s === "critical") return 0;
  if (s === "high") return 1;
  if (s === "medium") return 2;
  if (s === "low") return 3;
  return 4;
}
function prioTone(t) { return { overdue: 0, due: 1, open: 2, complete: 3 }[t] ?? 4; }
function fmt(v) { if (!v) return "—"; try { return new Date(v).toLocaleDateString(); } catch { return String(v); } }
